import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { CheckedNote } from './kit';
import type { Source } from './types';
import { tone } from '../primitives';

/** The numbered sources every [n] on the page points to. */
export default function SourcesList({
  name,
  sources,
}: {
  name: string;
  sources: Source[];
}) {
  return (
    <section
      id='sources'
      aria-labelledby='sources-title'
      className='scroll-mt-6 py-16 sm:py-20'
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-10 lg:grid-cols-12 lg:gap-16'>
          <div className='lg:col-span-4'>
            <h2
              id='sources-title'
              className={clsxm(
                'text-2xl font-semibold tracking-tight',
                tone.ink,
              )}
            >
              Sources
            </h2>
            <CheckedNote className='mt-3' />
            <p className={clsxm('mt-4 text-[15px] leading-7', tone.body)}>
              Every fact about {name} on this page comes from {name}’s own
              pricing page, docs or help center (or an archived copy of them),
              or from the company that owns it. Facts about Easy-Peasy.AI come
              from our product and{' '}
              <a
                href='https://easy-peasy.ai/docs/agents/overview'
                className='font-medium text-primary-700 underline-offset-4 hover:underline dark:text-primary-300'
              >
                docs
              </a>
              . Seen something out of date?{' '}
              <Link
                href='/contact'
                className='font-medium text-primary-700 underline-offset-4 hover:underline dark:text-primary-300'
              >
                Tell us
              </Link>
              .
            </p>
          </div>
          <ol className='grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:col-span-8'>
            {sources.map((source, index) => (
              <li
                key={source.id}
                id={`source-${index + 1}`}
                className='-mx-3 flex scroll-mt-6 gap-3 rounded-xl px-3 py-2.5 target:bg-primary-50 target:ring-1 target:ring-primary-200 dark:target:bg-primary-400/10 dark:target:ring-primary-400/30'
              >
                <span
                  className={clsxm(
                    'w-7 flex-none pt-px text-sm font-semibold tabular-nums',
                    tone.quiet,
                  )}
                >
                  [{index + 1}]
                </span>
                <span className='min-w-0'>
                  <span
                    className={clsxm('block text-[15px] font-medium', tone.ink)}
                  >
                    {source.label}
                  </span>
                  <a
                    href={source.url}
                    target='_blank'
                    rel='nofollow noopener noreferrer'
                    className='block break-all text-sm text-primary-700 underline-offset-4 hover:underline dark:text-primary-300'
                  >
                    {source.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
